import React from "react";
import { Icon } from "@chakra-ui/react";
import CDS from "design-system";
import { FiCheck, FiClock } from "react-icons/fi";
import { MdRemoveCircleOutline } from "react-icons/md";
import { format } from "date-fns";
import { absoluteDueLabel, relativeDueLabel } from "../helpers";
import type { CellState } from "../helpers/cellState";
import { COCKPIT } from "../theme/cockpit";

/**
 * Visual contract for a cell, derived from a CellState.
 *
 * `showBox` is the actionable checkbox-style affordance (click to complete);
 * `glyph` is a passive symbol (check, clock, em-dash, etc.). Exactly one of
 * the two paths renders per state — `chromeForState` is the single source of
 * truth for "what does this cell look like."
 */
export interface CellChrome {
  borderColor?: string;
  bgFill?: string;
  glyph?: React.ReactNode;
  showBox: boolean;
  primaryClickable: boolean;
}

export function chromeForState(state: CellState): CellChrome {
  switch (state.kind) {
    case "open":
      return { showBox: true, primaryClickable: true, borderColor: COCKPIT.open.border };
    case "due_soon":
      return { showBox: true, primaryClickable: true, borderColor: COCKPIT.amber.border };
    case "overdue":
      return {
        showBox: true,
        primaryClickable: true,
        borderColor: COCKPIT.red.mark,
        bgFill: COCKPIT.red.fill,
      };
    case "not_started":
      return {
        showBox: false,
        primaryClickable: false,
        glyph: <Icon as={FiClock} color={COCKPIT.resolved} boxSize={4} opacity={0.45} />,
      };
    case "done":
      return {
        showBox: false,
        primaryClickable: false,
        glyph: <Icon as={FiCheck} color={COCKPIT.resolved} boxSize={4} />,
      };
    case "skipped":
    case "closed_stale":
      return {
        showBox: false,
        primaryClickable: false,
        glyph: <Icon as={MdRemoveCircleOutline} color={COCKPIT.resolved} boxSize={4} />,
      };
    case "na":
    default:
      return {
        showBox: false,
        primaryClickable: false,
        glyph: <CDS.Text12 color={COCKPIT.resolved}>—</CDS.Text12>,
      };
  }
}

export function tooltipFor(state: CellState, today: Date): string {
  switch (state.kind) {
    case "open":
      return "Open";
    case "due_soon": {
      const rel = relativeDueLabel(state.dueAt, today);
      const lower = rel.toLowerCase();
      if (lower === "today") return "Due today";
      if (lower === "tomorrow") return "Due tomorrow";
      if (lower.startsWith("in ")) return `Due ${lower}`;
      return `Due · ${rel}`;
    }
    case "overdue":
      return `Overdue · ${state.daysLate} day${state.daysLate === 1 ? "" : "s"} late`;
    case "not_started":
      return state.startAt
        ? `Not started · starts ${format(state.startAt, "MMM d")}`
        : "Not started";
    case "done":
      return state.completedAt
        ? `Done · completed ${absoluteDueLabel(state.completedAt)}`
        : "Done";
    case "skipped":
      return "Skipped";
    case "closed_stale":
      return "Stale · auto-closed";
    case "na":
    default:
      return "";
  }
}
